import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RECIPIENT_HINT, classifyRecipient } from '@fleet/shared';
import {
  integrationsApi,
  type IntegrationStatus,
  type IntegrationTestResult,
} from '../../api/integrations';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { Field, inputClass } from '../../ui/Field';
import { MessageTemplates } from './MessageTemplates';

/**
 * Integrationen prüfen.
 *
 * Der Anlass ist eine Lücke, die `docs/slack-setup.md` selbst benennt: Bis
 * hierher ließ sich eine Slack-Einrichtung nur daran ablesen, ob im Dialog
 * „Slack verknüpfen" eine Mitgliederliste erscheint — das prüft das Bot-Token und
 * sonst nichts. Für E-Mail gab es gar keinen Weg, weil es keinen Versender gab.
 *
 * Nur für die Administration, wie „Nutzer" und „Rohdaten": Hier stehen Angaben
 * aus der Konfiguration, und ein Knopf schickt Nachrichten an frei gewählte
 * Empfänger.
 */
export function IntegrationsPage() {
  const query = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: () => integrationsApi.status(),
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Integrationen</h2>
        <p className="max-w-3xl text-sm text-slate-500">
          Geprüft werden Zugangsdaten und der Weg nach draußen —{' '}
          <strong className="font-medium">nicht</strong> die Benachrichtigungskette. Ein grüner Test
          sagt, dass die Anlage senden kann; dass eine Buchung von selbst eine Nachricht auslöst,
          sagt er nicht. Dafür fehlt die Anmeldung der Zustellwege aus Etappe 6.
        </p>
      </div>

      <AsyncBoundary query={query}>
        {(channels) => (
          <div className="grid items-start gap-4 lg:grid-cols-2">
            {channels.map((status) => (
              <ChannelCard key={status.channel} status={status} />
            ))}
          </div>
        )}
      </AsyncBoundary>

      {/*
        Die Vorlagen stehen unter den Kanälen und nicht in einem eigenen Reiter:
        Sie sind global wie die Kanäle und gehören zur selben Frage — was geht
        hinaus, und wie sieht es aus.
      */}
      <div className="border-t border-slate-200 pt-6">
        <MessageTemplates />
      </div>
    </div>
  );
}

/** Was je Kanal anders ist — alles Übrige teilen die beiden Karten. */
const CHANNELS = {
  SLACK: {
    title: 'Slack',
    recipientLabel: 'Empfänger',
    recipientHint: `${RECIPIENT_HINT} Ohne Eingabe geht die Nachricht an dein verknüpftes Konto.`,
    placeholder: 'C01ABC2DEF',
    inputType: 'text',
    check: integrationsApi.checkSlack,
    test: integrationsApi.testSlack,
    /**
     * Der Channel-Test ist der, der sich lohnt: Ohne Einladung des Bots sieht
     * alles andere richtig eingerichtet aus, und `not_in_channel` erscheint erst
     * beim ersten echten Versand.
     */
    advice: 'Eine Channel-ID einzutragen prüft zusätzlich, ob der Bot dort eingeladen ist.',
  },
  EMAIL: {
    title: 'E-Mail',
    recipientLabel: 'Empfänger',
    recipientHint: 'Ohne Eingabe geht die Mail an deine eigene Adresse.',
    placeholder: 'name@beans.de',
    inputType: 'email',
    check: integrationsApi.checkEmail,
    test: integrationsApi.testEmail,
    advice:
      'E-Mail ist bislang nur ein Versender für diesen Test — es werden noch keine Buchungsmails verschickt.',
  },
} as const;

function ChannelCard({ status }: { status: IntegrationStatus }) {
  const channel = CHANNELS[status.channel];

  /**
   * Vorbelegt mit der eigenen Adresse bzw. dem eigenen Slack-Konto: Die harmlose
   * Vorgabe ist der Normalfall, und wer woanders hin testen will, ändert sie.
   */
  const [recipient, setRecipient] = useState(status.defaultRecipient ?? '');

  const check = useMutation({ mutationFn: () => channel.check() });
  const test = useMutation({ mutationFn: () => channel.test(recipient.trim() || undefined) });

  /**
   * Beim Slack-Test wird die Eingabe schon hier beurteilt — mit derselben
   * Funktion, die der Server benutzt. Eine Usergruppe (`S…`) oder ein
   * hingeschriebener Channelname würde sonst bis zu Slack laufen und mit einer
   * Meldung zurückkommen, die von der Eingabe nicht mehr spricht.
   */
  const typed = recipient.trim();
  const unrecognized =
    status.channel === 'SLACK' && typed.length > 0 && classifyRecipient(typed) === undefined;

  const busy = check.isPending || test.isPending;

  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-medium">{channel.title}</h3>
        {status.isConfigured ? (
          <Badge tone="emerald">eingerichtet</Badge>
        ) : (
          <Badge tone="amber">nicht eingerichtet</Badge>
        )}
      </div>

      <dl className="space-y-1 text-sm">
        {status.facts.map((fact) => (
          <div key={fact.label} className="flex gap-2">
            <dt className="w-36 shrink-0 text-slate-500">{fact.label}</dt>
            <dd className="min-w-0 break-words">{fact.value}</dd>
          </div>
        ))}
      </dl>

      {/*
        Die fehlenden Schlüssel beim Namen zu nennen ist der ganze Zweck der
        Karte im unkonfigurierten Zustand: „nicht eingerichtet" schickt jemanden
        die Konfiguration durchsuchen, „es fehlt SMTP_FROM" nicht.
      */}
      {status.missing.length > 0 && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Es fehlt in der Konfiguration:{' '}
          {status.missing.map((key, index) => (
            <span key={key}>
              {index > 0 && ', '}
              <code className="font-mono">{key}</code>
            </span>
          ))}
        </p>
      )}

      {/*
        Warnungen sind keine Fehler: Sie beschreiben, was die eingestellte
        Konfiguration tatsächlich tut. „Die Mail geht im Klartext heraus" ist eine
        zulässige Betriebsweise im eigenen Netz — nur soll niemand sie für etwas
        anderes halten.
      */}
      {status.warnings.map((warning) => (
        <p key={warning} className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700">
          {warning}
        </p>
      ))}

      <p className="text-xs text-slate-500">{channel.advice}</p>

      <Field
        label={channel.recipientLabel}
        hint={channel.recipientHint}
        optional
        error={unrecognized ? RECIPIENT_HINT : undefined}
      >
        <input
          type={channel.inputType}
          value={recipient}
          onChange={(event) => setRecipient(event.target.value)}
          placeholder={channel.placeholder}
          disabled={!status.isConfigured || busy}
          className={inputClass}
        />
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          disabled={!status.isConfigured || busy}
          onClick={() => {
            test.reset();
            check.mutate();
          }}
        >
          {check.isPending ? 'Wird geprüft …' : 'Zugang prüfen'}
        </Button>
        <Button
          variant="primary"
          disabled={!status.isConfigured || busy || unrecognized}
          onClick={() => {
            check.reset();
            test.mutate();
          }}
        >
          {test.isPending ? 'Wird gesendet …' : 'Testnachricht senden'}
        </Button>
      </div>

      {/*
        Zwei Wege hinein, und sie bedeuten Verschiedenes: Ein `ApiError` heißt,
        dass die Anfrage nicht durchkam — fehlende Rolle, nicht eingerichteter
        Kanal. Ein Ergebnis mit `ok: false` heißt, dass die Anfrage durchkam und
        die Antwort „nein" lautet. Nur das Zweite nennt einen Slack- oder
        SMTP-Code.
      */}
      {check.isError && <ErrorNotice error={check.error} />}
      {test.isError && <ErrorNotice error={test.error} />}
      {check.data && <Outcome result={check.data} />}
      {test.data && <Outcome result={test.data} />}
    </section>
  );
}

function Outcome({ result }: { result: IntegrationTestResult }) {
  return (
    <div
      role="status"
      className={[
        'space-y-2 rounded-md px-3 py-2 text-sm',
        result.ok ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-900',
      ].join(' ')}
    >
      <p>{result.message}</p>

      {result.facts && result.facts.length > 0 && (
        <dl className="space-y-0.5 text-xs">
          {result.facts.map((fact) => (
            <div key={fact.label} className="flex gap-2">
              <dt className="w-28 shrink-0 opacity-70">{fact.label}</dt>
              <dd className="min-w-0 break-all font-mono">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {/*
        Der Rohcode steht dabei, auch wenn der Satz darüber ihn erklärt: Mit ihm
        findet man Slacks oder nodemailers Dokumentation, ohne ihn fängt die
        Suche von vorn an.
      */}
      {!result.ok && result.code && (
        <p className="text-xs">
          Code: <code className="font-mono">{result.code}</code>
        </p>
      )}
    </div>
  );
}
